import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useState } from 'react';
import './Login.css';

const Login = () => {
  const { currentUser, login } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (data) => {
    setLoginError('');
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        setLoginError(result.error || 'Invalid email or password. Please try again.');
      }
    } catch (err) {
      setLoginError('Failed to log in. Please check your credentials and try again.');
      console.error(err);
    }
  };

  useEffect(() => {
    currentUser && navigate('/dashboard');
  }, [currentUser, navigate]);

  useEffect(() => {
    document.title = 'Login | Streamline';
  }, []);

  return (
    <div className="auth-container">
      {/* Animated Background */}
      <div className="scene-container">
        <div className="grid-overlay"></div>
        <div className="glow-effect"></div>
        <div className="floating-elements">
          <div className="float-element"></div>
          <div className="float-element"></div>
          <div className="float-element"></div>
        </div>
      </div>

      <div className="auth-content">
        {/* Left side - Branding */}
        <div className="auth-left">
          <div className="brand-info">
            <div className="logo-container">
              <svg className="app-logo" viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="2" fill="none">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
            </div>
            <h1 className="app-title">Streamline</h1>
            <p className="app-subtitle">Maximize Your Productivity</p>
            <div className="brand-description">
              <p>Experience a new way of managing tasks with our modern and intuitive platform.</p>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="auth-right">
          <div className="auth-form-container">
            <h1>Welcome Back</h1>
            <p className="auth-subtitle">
              Sign in to continue to your workspace
            </p>

            {loginError && (
              <div className="error-message">
                {loginError}
              </div>
            )}

            <form onSubmit={handleSubmit(handleLogin)} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="Enter your email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                />
                {errors.email && <span className="input-error">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  placeholder="Enter your password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 2,
                      message: "Password must be at least 2 characters"
                    }
                  })}
                />
                {errors.password && <span className="input-error">{errors.password.message}</span>}
              </div>

              <div className="form-options">
                <label className="checkbox-container">
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                  Remember me
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="loading-spinner"></div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                Don't have an account? <Link to="/register" className="auth-link">Sign up</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
